import { useCallback, useEffect, useState } from "react";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import { Alert, Badge, Button, EmptyState, LoadingState } from "@/components/ui";
import { ApiError, apiErrorMessage } from "@/api/client";
import type { CommunityRemoval, DeskApi } from "./deskApi";
import { fmtDate, PersonChip } from "./DeskShared";
import styles from "./desk.module.css";

const KIND: Record<CommunityRemoval["subject_type"], string> = {
  post: "Post",
  comment: "Comment",
  chat_message: "Chat message",
};

/** A remover with this many removals in 30 days is worth a look (not a rule, a prompt). */
const BUSY_REMOVER = 20;

/**
 * Removals made by community Founders, Admins and Moderators inside their own
 * communities (i107), with the removed words for context. This is HQ's view of
 * moderator behaviour: who removed what, from whom, and why.
 */
export function CommunityRemovalsTab({ api }: { api: DeskApi }) {
  const [items, setItems] = useState<CommunityRemoval[] | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.communityRemovals({ limit: 25, offset });
      setItems(res.items);
      setTotal(res.total);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) setForbidden(true);
      else setError(apiErrorMessage(e));
    }
  }, [api, offset]);
  useEffect(() => { void load(); }, [load]);

  if (forbidden) {
    return <EmptyState title="Not available to you" description="This list shows every community's moderator activity. Ask an Admin for access to everyone's work." />;
  }
  if (error) {
    return (
      <Alert tone="danger" title="Could not load community removals">
        {error} <Button variant="ghost" onClick={() => void load()}>Try again</Button>
      </Alert>
    );
  }
  if (!items) return <LoadingState />;
  if (items.length === 0) {
    return (
      <EmptyState
        title="No community removals yet"
        description="When a community Founder, Admin or Moderator removes someone's post, comment or chat message, it is recorded here with who did it and why."
      />
    );
  }

  return (
    <Stack gap={12}>
      {items.map((r) => {
        const words = [r.snapshot?.title, r.snapshot?.body].filter((v) => typeof v === "string" && v).join(" · ");
        return (
          <div key={r.id} className={styles.panel}>
            <Flex gap={8} wrap align="center">
              <Badge>{KIND[r.subject_type]}</Badge>
              <Text role="label" as="span">{r.club.handle ?? r.club.name ?? "Community"}</Text>
              <Text role="caption" color="muted" as="span">{fmtDate(r.removed_at)}</Text>
              {r.removed_by_count_30d >= BUSY_REMOVER ? <Badge tone="warning">{r.removed_by_count_30d} removals in 30 days</Badge> : null}
            </Flex>
            <Flex gap={16} wrap>
              <Stack gap={4}>
                <Text role="caption" color="secondary" as="span">Removed by ({r.removed_by_role})</Text>
                {r.removed_by ? <PersonChip person={r.removed_by} /> : <Text role="small" as="span">Unknown</Text>}
              </Stack>
              <Stack gap={4}>
                <Text role="caption" color="secondary" as="span">Author</Text>
                {r.author ? <PersonChip person={r.author} /> : <Text role="small" as="span">Unknown</Text>}
              </Stack>
            </Flex>
            <Text role="small" as="p">{words || "(no text, for example a photo only)"}</Text>
            <Text role="caption" color="muted" as="p">{r.reason ? `Reason given: ${r.reason}` : "No reason given."}</Text>
          </div>
        );
      })}
      <Flex gap={8} align="center">
        <Button variant="secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 25))}>Previous</Button>
        <Text role="caption" color="muted" as="span">{offset + 1} to {Math.min(offset + 25, total)} of {total}</Text>
        <Button variant="secondary" disabled={offset + 25 >= total} onClick={() => setOffset(offset + 25)}>Next</Button>
      </Flex>
    </Stack>
  );
}
